/**
 * Orders work panel — order vs script.
 *
 * Order row opens the order panel (vendor, order meta, every script with
 * status/due, order-level activity). Script row opens the script panel
 * (title/hook/synopsis/full script, status editor, script activity).
 * J/K and arrows move between scripts inside the open order without closing.
 */
export type OrdersPanelKind = "order" | "script";

export type OrdersPanelTarget =
  | { kind: "order"; orderId: string }
  | { kind: "script"; orderId: string; scriptId: string };

export const ORDERS_PANEL_WIDTH_MIN = 480;
export const ORDERS_PANEL_WIDTH_MAX = 840;
export const ORDERS_PANEL_WIDTH_DEFAULT = 600;
export const ORDERS_PANEL_WIDTH_STORAGE_KEY = "marketplace:orders:detailPanelWidth";

export function clampPanelWidth(width: number): number {
  return Math.min(
    ORDERS_PANEL_WIDTH_MAX,
    Math.max(ORDERS_PANEL_WIDTH_MIN, Math.round(width)),
  );
}

export function loadPanelWidth(): number {
  if (typeof window === "undefined") return ORDERS_PANEL_WIDTH_DEFAULT;
  try {
    const stored = Number(window.localStorage.getItem(ORDERS_PANEL_WIDTH_STORAGE_KEY));
    if (Number.isFinite(stored) && stored > 0) return clampPanelWidth(stored);
  } catch {
    // Private browsing can block localStorage; the default width still works.
  }
  return ORDERS_PANEL_WIDTH_DEFAULT;
}

export function persistPanelWidth(width: number): void {
  try {
    window.localStorage.setItem(
      ORDERS_PANEL_WIDTH_STORAGE_KEY,
      String(clampPanelWidth(width)),
    );
  } catch {
    // A persistence miss must not block resizing in memory.
  }
}

export function panelTargetFromSearch(search: {
  order?: string;
  script?: string;
}): OrdersPanelTarget | null {
  if (search.script) {
    return { kind: "script", orderId: search.order ?? "", scriptId: search.script };
  }
  if (search.order) return { kind: "order", orderId: search.order };
  return null;
}

export function resolvePanelTarget(
  target: OrdersPanelTarget | null,
  orders: ReadonlyArray<{ id: string; scripts: ReadonlyArray<{ id: string }> }>,
): OrdersPanelTarget | null {
  if (!target) return null;
  if (target.kind === "script") {
    const order =
      (target.orderId
        ? orders.find((candidate) => candidate.id === target.orderId)
        : undefined) ??
      orders.find((candidate) =>
        candidate.scripts.some((script) => script.id === target.scriptId),
      );
    if (!order) return null;
    const script = order.scripts.find((row) => row.id === target.scriptId);
    if (!script) return { kind: "order", orderId: order.id };
    return { kind: "script", orderId: order.id, scriptId: script.id };
  }
  return orders.some((order) => order.id === target.orderId) ? target : null;
}

export function adjacentScriptTarget(
  target: OrdersPanelTarget,
  scripts: ReadonlyArray<{ id: string }>,
  delta: -1 | 1,
): OrdersPanelTarget | null {
  if (scripts.length === 0) return null;
  if (target.kind === "order") {
    const script = delta === 1 ? scripts[0] : scripts[scripts.length - 1];
    return script
      ? { kind: "script", orderId: target.orderId, scriptId: script.id }
      : null;
  }
  const index = scripts.findIndex((script) => script.id === target.scriptId);
  const next = scripts[index + delta];
  if (!next) return null;
  return { kind: "script", orderId: target.orderId, scriptId: next.id };
}

export function panelSearchPatch(target: OrdersPanelTarget | null): {
  order: string | undefined;
  script: string | undefined;
} {
  if (!target) return { order: undefined, script: undefined };
  if (target.kind === "order") return { order: target.orderId, script: undefined };
  return { order: target.orderId, script: target.scriptId };
}
